import React, { useEffect, useMemo, useState } from 'react';
import { Box, Button, Container, TextField, Typography, Paper, Table, TableHead, TableRow, TableCell, TableBody, Divider, Chip, Stack } from '@mui/material';
import { api } from '../services/api';

type UserSummary = {
  id: string;
  username: string;
  email: string;
  created_at?: string;
  last_login?: string;
  is_active: boolean;
};

type ConversationItem = {
  id: string;
  category?: string;
  original_question?: string;
  status?: string;
  confidence_level?: string;
  created_at?: string;
  updated_at?: string;
  model_used?: string;
  processing_time?: number;
  has_final_response?: boolean;
  metadata?: any;
};

type DocumentItem = {
  filename: string;
  path: string;
  contract_type: string;
  modified_time: number;
};

const AdminPage: React.FC = () => {
  const [secret, setSecret] = useState<string>(() => localStorage.getItem('adminSecret') || '');
  const [users, setUsers] = useState<UserSummary[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [emailQuery, setEmailQuery] = useState('');
  const [activityUser, setActivityUser] = useState<any>(null);
  const [conversations, setConversations] = useState<ConversationItem[]>([]);
  const [documents, setDocuments] = useState<DocumentItem[]>([]);
  const [activityLoading, setActivityLoading] = useState(false);
  const [error, setError] = useState<string>('');

  const headers = useMemo(() => ({ 'X-Admin-Secret': secret }), [secret]);

  const loadUsers = async () => {
    if (!secret) { setError('Zadejte prosím admin secret.'); return; }
    setLoadingUsers(true);
    setError('');
    try {
      const res = await api.get<UserSummary[]>('/admin/users', { headers });
      setUsers(res.data || []);
    } catch (e: any) {
      setError(e?.response?.data?.detail || 'Nepodařilo se načíst seznam uživatelů.');
    } finally {
      setLoadingUsers(false);
    }
  };

  const loadActivity = async (email: string) => {
    if (!secret) { setError('Zadejte prosím admin secret.'); return; }
    if (!email) { setError('Zadejte e‑mail uživatele.'); return; }
    setActivityLoading(true);
    setError('');
    try {
      const res = await api.get('/admin/user-activity', { params: { email }, headers });
      setActivityUser(res.data.user);
      setConversations(res.data.conversations || []);
      setDocuments(res.data.documents || []);
    } catch (e: any) {
      setActivityUser(null);
      setConversations([]);
      setDocuments([]);
      setError(e?.response?.data?.detail || 'Nepodařilo se načíst aktivitu uživatele.');
    } finally {
      setActivityLoading(false);
    }
  };

  useEffect(() => {
    if (secret) loadUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const saveSecret = () => {
    localStorage.setItem('adminSecret', secret);
    loadUsers();
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Paper elevation={0} sx={{
        p: 2.5,
        mb: 2,
        borderRadius: 2,
        background: 'linear-gradient(135deg, #0f172a 0%, #334155 45%, #0ea5e9 100%)',
        color: '#fff'
      }}>
        <Typography variant="h4" sx={{ fontWeight: 900, mb: 0.5 }}>Admin přehled</Typography>
        <Typography variant="body2" sx={{ opacity: 0.9 }}>Zadejte admin secret a pracujte s testovacími účty.</Typography>
      </Paper>

      <Paper sx={{ p: 2, mb: 3 }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ xs: 'stretch', sm: 'center' }}>
          <TextField
            label="Admin Secret"
            value={secret}
            onChange={(e) => setSecret(e.target.value)}
            fullWidth
            size="small"
            type="password"
          />
          <Button variant="contained" onClick={saveSecret} disabled={!secret}>Uložit a načíst uživatele</Button>
          <Button variant="outlined" onClick={loadUsers} disabled={!secret || loadingUsers}>Obnovit</Button>
        </Stack>
        {error && (
          <Typography sx={{ mt: 1, color: 'error.main' }}>{error}</Typography>
        )}
      </Paper>

      <Paper sx={{ p: 2, mb: 3 }}>
        <Typography variant="h6" sx={{ fontWeight: 800, mb: 1 }}>Uživatelé</Typography>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: '#F7FAFF' }}>
              <TableCell sx={{ fontWeight: 700, color: '#0f172a' }}>Username</TableCell>
              <TableCell sx={{ fontWeight: 700, color: '#0f172a' }}>Email</TableCell>
              <TableCell sx={{ fontWeight: 700, color: '#0f172a' }}>Vytvořen</TableCell>
              <TableCell sx={{ fontWeight: 700, color: '#0f172a' }}>Poslední přihlášení</TableCell>
              <TableCell sx={{ fontWeight: 700, color: '#0f172a' }}>Stav</TableCell>
              <TableCell align="right" sx={{ fontWeight: 700, color: '#0f172a' }}>Akce</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {users.map(u => (
              <TableRow key={u.id} hover>
                <TableCell>{u.username}</TableCell>
                <TableCell>{u.email}</TableCell>
                <TableCell>{u.created_at || '-'}</TableCell>
                <TableCell>{u.last_login || '-'}</TableCell>
                <TableCell>
                  <Chip 
                    size="small" 
                    label={u.is_active ? 'aktivní' : 'neaktivní'} 
                    color={u.is_active ? 'success' : 'error'}
                    variant={u.is_active ? 'filled' : 'outlined'}
                    sx={{
                      fontWeight: 700,
                      ...(u.is_active ? { bgcolor: '#16a34a', color: '#fff' } : { color: '#b91c1c', borderColor: '#b91c1c' })
                    }}
                  />
                </TableCell>
                <TableCell align="right">
                  <Button 
                    size="small" 
                    variant="contained"
                    color="secondary"
                    onClick={() => loadActivity(u.email)}
                    sx={{ fontWeight: 700 }}
                  >
                    Aktivita
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>

      <Paper sx={{ p: 2, mb: 3 }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ xs: 'stretch', sm: 'center' }}>
          <TextField label="E‑mail uživatele" value={emailQuery} onChange={(e) => setEmailQuery(e.target.value)} size="small" fullWidth />
          <Button variant="contained" onClick={() => loadActivity(emailQuery)} disabled={activityLoading || !emailQuery}>Načíst aktivitu</Button>
        </Stack>
        {activityUser && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Uživatel</Typography>
            <Typography variant="body2">{activityUser.username} ({activityUser.email})</Typography>
            <Typography variant="body2" sx={{ color: '#666' }}>UUID: {activityUser.id} | numeric_id: {activityUser.numeric_id || '-'}</Typography>
          </Box>
        )}
        <Divider sx={{ my: 2 }} />

        <Typography variant="h6" sx={{ fontWeight: 900, mb: 1 }}>Konverzace</Typography>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>ID</TableCell>
              <TableCell>Kategorie</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Model</TableCell>
              <TableCell>Vytvořeno</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {conversations.map(c => (
              <TableRow key={c.id}>
                <TableCell sx={{ maxWidth: 220, whiteSpace: 'nowrap', textOverflow: 'ellipsis', overflow: 'hidden' }}>{c.id}</TableCell>
                <TableCell>{c.category || '-'}</TableCell>
                <TableCell>{c.status || '-'}</TableCell>
                <TableCell>{c.model_used || '-'}</TableCell>
                <TableCell>{c.created_at || '-'}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>

        <Divider sx={{ my: 2 }} />

        <Typography variant="h6" sx={{ fontWeight: 900, mb: 1 }}>Dokumenty</Typography>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Název</TableCell>
              <TableCell>Typ</TableCell>
              <TableCell>Čas</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {documents.map((d) => (
              <TableRow key={d.filename}>
                <TableCell>{d.filename}</TableCell>
                <TableCell>{d.contract_type}</TableCell>
                <TableCell>{new Date(d.modified_time * 1000).toLocaleString()}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>
    </Container>
  );
};

export default AdminPage;
